/**
 * AlertService
 * 
 * Handles alert operations, ensuring all alerts are associated with the current authenticated user.
 * Provides methods for creating, updating, deleting, and retrieving user-specific alerts.
 * Integrates with BaseRepository and DatabaseService for persistence.
 * Includes alert evaluation logic for processing incoming events against alert rules.
 * Uses NotificationDispatcher to handle notification creation and delivery.
 * 
 * @module AlertService
 */
import BaseRepository from './BaseRepository';
import { DatabaseService, FULL_ALERT_SYSTEM_SCHEMA } from './DatabaseService';
import { getCurrentUser, addUserInfoToObject } from '../utils/AuthUtils';
import RuleEvaluator from '../alerts/RuleEvaluator';
import NotificationDispatcher from '../alerts/NotificationDispatcher';
import { mockUserData } from './mockData';

// Enable auth bypass for local development to allow using the app without authentication
const AUTH_BYPASS = true;

const ALERT_STORE = 'alerts';

/**
 * Default database instance used when none is provided
 * Use the full schema to ensure all required object stores are created
 * @type {DatabaseService}
 */
const defaultDbService = new DatabaseService('DeepRDashboardDB', 2, FULL_ALERT_SYSTEM_SCHEMA);

class AlertService {
  /**
   * @param {DatabaseService} dbService - Instance of DatabaseService
   */
  constructor(dbService = defaultDbService) {
    this.dbService = dbService;
    this.repository = new BaseRepository(dbService, ALERT_STORE);
    this.cachedAlerts = null;
    this.cacheTimestamp = null;
    this.cacheMaxAge = 60000; // Cache valid for 1 minute
    this.schemaInitialized = false;
    
    // Initialize the rule evaluator
    this.ruleEvaluator = new RuleEvaluator();
    
    // Initialize the notification dispatcher with desktop notifications explicitly enabled
    this.notificationDispatcher = new NotificationDispatcher(null, {
      enableDesktopNotifications: true,
      deduplicationWindow: 5000, // 5 seconds - reduced from 60 seconds to allow more frequent notifications
      throttleWindow: 300000, // 5 minutes
      maxNotificationsPerWindow: 20 // Increased from 10 to allow more notifications per window
    });
    
    // Initialize the database
    this.initializeDatabase();
  }
  
  /**
   * Initialize the database, ensuring all required object stores exist
   * If schema issues are detected, will attempt to reset and rebuild the database
   * @returns {Promise<void>}
   */
  async initializeDatabase() {
    try {
      // First try normal connection
      await this.dbService.connect();
      this.schemaInitialized = true;

    } catch (error) {
      console.error('Error initializing database:', error);
      
      if (error.message && error.message.includes('object store')) {
        console.warn('Schema mismatch detected, attempting database reset and rebuild...');
        try {
          // Force reset and recreate the database with the correct schema
          await this.dbService.connect(true);
          this.schemaInitialized = true;
    
        } catch (resetError) {
          console.error('Failed to reset and rebuild database:', resetError);
          throw new Error('Database initialization failed after reset attempt: ' + resetError.message);
        }
      } else {
        throw error;
      }
    }
  }

  /**
   * Ensure database is initialized before performing operations
   * @returns {Promise<void>}
   * @private
   */
  async _ensureDatabaseReady() {
    if (!this.schemaInitialized) {

      await this.initializeDatabase();
    }
  }

  /**
   * Create a new alert associated with the current user
   * @param {Object} alert - Alert definition object
   * @returns {Promise<IDBValidKey>} - Returns the key of the created alert
   */
  async createAlert(alert) {

    await this._ensureDatabaseReady();
    
    // Get the current user
    const user = getCurrentUser();
    
    if (!user) {
      console.error('ERROR: No authenticated user found');
      throw new Error('User authentication is required to create alerts');
    }
    
    // Add user information to the alert
    const alertWithUser = addUserInfoToObject(alert);
    
    // Add timestamps
    const now = new Date().toISOString();
    alertWithUser.created_at = alertWithUser.created_at || now;
    alertWithUser.updated_at = now;
    
    // Ensure the alert has a unique ID
    if (!alertWithUser.id) {
      alertWithUser.id = 'alert_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      console.log(`Generated unique ID for alert: ${alertWithUser.id}`);
    }
    
    // Ensure the alert is valid
    if (!alertWithUser.name) {
      throw new Error('Alert name is required');
    }
    
    if (!alertWithUser.type) {
      throw new Error('Alert type is required');
    }
    
    // Ensure the alert has rules
    if (!alertWithUser.rules || !Array.isArray(alertWithUser.rules) || alertWithUser.rules.length === 0) {
      throw new Error('Alert must have at least one rule');
    }
    
    // Validate process name targeting options for Process type alerts
    if (alertWithUser.type === 'Process') {
      for (const rule of alertWithUser.rules) {
        if (rule.processNameTargetingEnabled && (!rule.processNamePattern || rule.processNamePattern.trim() === '')) {
          throw new Error('Process name pattern is required when process name targeting is enabled');
        }
      }
    }
    
    // Log the alert being created

    
    try {
      // Create the alert in the repository
      const alertId = await this.repository.create(alertWithUser);

      
      // Invalidate the cache
      this.invalidateCache();
      

      return alertId;
    } catch (error) {
      console.error('ERROR: Failed to create alert in repository:', error);


      throw error;
    }
  }

  /**
   * Utility: Remove all alerts with invalid condition strings (e.g. 'greater_than')
   * Call this before evaluating alerts to prevent legacy errors.
   */
  async removeInvalidAlerts() {
    try {
      const alerts = await this.getUserAlerts();
      let invalidAlertCount = 0;
      
      for (const alert of alerts) {
        if (alert.rules && Array.isArray(alert.rules)) {
          for (const rule of alert.rules) {
            if (rule.condition && typeof rule.condition === 'string' && rule.condition.includes('_')) {
              console.warn(`Found alert with invalid condition format: ${alert.id}, condition: ${rule.condition}`);
              await this.deleteAlert(alert.id);
              invalidAlertCount++;
              break; // Break after first invalid rule in an alert
            }
          }
        }
      }
      

    } catch (error) {
      console.error('Error removing invalid alerts:', error);
    }
  }

  /**
   * Retrieve all alerts for the current user
   * @returns {Promise<Object[]>} - Returns array of alerts belonging to current user
   */
  async getUserAlerts() {
    // NOTE: Cleanup of invalid alerts is intentionally NOT run on every alert fetch for performance reasons.
    // If you need to remove legacy/invalid alerts, call removeInvalidAlerts() ONCE during app startup or as a migration step.
    // This avoids repeated and unnecessary database writes and log spam.
    // See: https://github.com/deepranalytics/DeepRDashboard/issues/cleanup-policy for rationale (if documented).
    // (Previous code: await this.removeInvalidAlerts();)
    const user = getCurrentUser();

    
    if (!user) {
      console.error('ERROR: No authenticated user found');
      throw new Error('User authentication is required to get alerts');
    }
    
    try {
      // Find all alerts
      const allAlerts = await this.repository.findAll();

      
      // Filter alerts for the current user
      const userAlerts = allAlerts.filter(alert => {
        // Check for matches with either property
        const matchesUserId = alert.user_id === user.user_id || alert.userId === user.user_id;
        return matchesUserId;
      });
      


      return userAlerts;
    } catch (error) {
      console.error('ERROR in getUserAlerts:', error);

      return [];
    }
  }

  /**
   * Update an alert (must belong to the current user)
   * @param {string} alertId - ID of the alert to update
   * @param {Object} updates - Object containing properties to update
   * @returns {Promise<Object>} - Returns the updated alert object
   */
  async updateAlert(alertId, updates) {
    await this._ensureDatabaseReady();


    
    // Get the current user
    const user = getCurrentUser();
    
    if (!user) {
      console.error('ERROR: No authenticated user found');
      throw new Error('User authentication is required to update alerts');
    }
    
    // Get the existing alert
    const existingAlert = await this.repository.get(alertId);
    
    if (!existingAlert) {
      throw new Error(`Alert with ID ${alertId} not found`);
    }
    
    // Check if the alert belongs to the current user
    if (existingAlert.userId !== user.id && existingAlert.user_id !== user.user_id) {
      console.error(`ERROR: Alert ${alertId} does not belong to the current user`);
      throw new Error('You are not authorized to update this alert');
    }
    
    // Update the alert
    const updatedAlert = { ...existingAlert, ...updates, updated_at: new Date().toISOString() };
    
    // Validate process name targeting options for Process type alerts
    if (updatedAlert.type === 'Process' && updatedAlert.rules && Array.isArray(updatedAlert.rules)) {
      for (const rule of updatedAlert.rules) {
        if (rule.processNameTargetingEnabled && (!rule.processNamePattern || rule.processNamePattern.trim() === '')) {
          throw new Error('Process name pattern is required when process name targeting is enabled');
        }
      }
    }
    

    
    try {
      await this.repository.update(alertId, updatedAlert);

      
      // Invalidate the cache
      this.invalidateCache();
      

      return updatedAlert;
    } catch (error) {
      console.error('ERROR: Failed to update alert in repository:', error);


      throw error;
    }
  }

  /**
   * Delete an alert (must belong to the current user)
   * @param {string} alertId - ID of the alert to delete
   * @returns {Promise<boolean>} - Returns true if deletion was successful
   */
  async deleteAlert(alertId) {
    await this._ensureDatabaseReady();


    
    // Get the current user
    const user = getCurrentUser();
    
    if (!user) {
      console.error('ERROR: No authenticated user found');
      throw new Error('User authentication is required to delete alerts');
    }
    
    // Get the existing alert
    const existingAlert = await this.repository.get(alertId);
    
    if (!existingAlert) {
      throw new Error(`Alert with ID ${alertId} not found`);
    }
    
    // Check if the alert belongs to the current user
    if (existingAlert.userId !== user.id && existingAlert.user_id !== user.user_id) {
      console.error(`ERROR: Alert ${alertId} does not belong to the current user`);
      throw new Error('You are not authorized to delete this alert');
    }
    
    try {
      await this.repository.delete(alertId);

      
      // Invalidate the cache
      this.invalidateCache();
      

      return true;
    } catch (error) {
      console.error('ERROR: Failed to delete alert from repository:', error);


      throw error;
    }
  }

  /**
   * Invalidate the alerts cache
   */
  invalidateCache() {
    this.cachedAlerts = null;
    this.cacheTimestamp = null;
  }

  /**
   * Get alerts by type
   * @param {string} type - The alert type to filter by
   * @returns {Promise<Object[]>} - Returns array of alerts of the specified type
   */
  async getAlertsByType(type) {
    const alerts = await this.getUserAlerts();
    return alerts.filter(alert => alert.type === type);
  }

  /**
   * Get alerts by severity
   * @param {string} severity - The severity level to filter by
   * @returns {Promise<Object[]>} - Returns array of alerts with the specified severity
   */
  async getAlertsBySeverity(severity) {
    const alerts = await this.getUserAlerts();
    return alerts.filter(alert => alert.severity === severity);
  }

  /**
   * Get alerts by server ID
   * @param {string} serverId - The server ID to filter by
   * @returns {Promise<Object[]>} - Returns array of alerts for the specified server
   */
  async getAlertsByServer(serverId) {
    const alerts = await this.getUserAlerts();
    return alerts.filter(alert => 
      alert.scope.type === 'server-specific' && 
      alert.scope.serverIds && 
      alert.scope.serverIds.includes(serverId)
    );
  }

  /**
   * Get all enabled alerts (cached for performance)
   * @returns {Promise<Object[]>} - Returns array of enabled alerts
   */
  async getEnabledAlerts() {
    const alerts = await this.getUserAlerts();
    return alerts.filter(alert => alert.enabled);
  }

  /**
   * Evaluate an event against all applicable alerts and create notifications for triggered alerts
   * @param {Object} event - The event to evaluate
   * @param {string} eventType - The type of event ('Global', 'SystemEvent', 'Process', 'Log')
   * @param {string} [serverId] - Optional server ID for server-specific alerts
   * @returns {Promise<Object>} - Returns object with triggeredAlerts and notifications
   */
  async evaluateEvent(event, eventType, serverId = null) {
    // Get all enabled alerts that match the event type
    const enabledAlerts = await this.getEnabledAlerts();
    
    // Filter alerts by type and server scope
    const applicableAlerts = enabledAlerts.filter(alert => {
      // Check if alert type matches event type
      if (alert.type !== eventType) {
        return false;
      }
      
      // For server-specific alerts, check if the server ID is included
      if (alert.scope.type === 'server-specific' && serverId) {
        return alert.scope.serverIds && alert.scope.serverIds.includes(serverId);
      }
      
      return true;
    });
    
    // Add process-specific filtering for Process type alerts with process name targeting enabled
    if (eventType === 'Process' && event.type === 'process') {
      // The actual process name filtering happens in RuleEvaluator.evaluateRule
      // This is just a debug log to show what's happening
      const processName = event.name || event.process_name;
      if (processName) {

      }
    }
    
    // Use the RuleEvaluator to process the batch of alerts against the event
    const { triggeredAlerts: triggeredAlertsMap } = this.ruleEvaluator.processBatch(
      applicableAlerts, 
      [event], 
      serverId
    );
    
    // Convert the map of triggered alerts to an array
    const triggeredAlerts = [];
    for (const alertId of Object.keys(triggeredAlertsMap)) {
      const alert = applicableAlerts.find(a => a.id === alertId);
      if (alert) {
        triggeredAlerts.push(alert);
      }
    }
    
    // If there are triggered alerts, create notifications
    let notifications = [];
    if (triggeredAlerts.length > 0) {
      notifications = await this.notificationDispatcher.dispatchNotifications(
        triggeredAlerts,
        event,
        eventType,
        serverId
      );
    }
    
    return {
      triggeredAlerts,
      notifications
    };
  }

  /**
   * Create and dispatch notifications from triggered alerts
   * @param {Object[]} triggeredAlerts - Array of triggered alerts
   * @param {Object} event - The event that triggered the alerts
   * @param {string} sourceType - The type of the event source
   * @param {string} [serverId] - Optional server ID for server-specific alerts
   * @returns {Promise<Object[]>} - Returns array of created notification objects
   */
  async createNotifications(triggeredAlerts, event, sourceType, serverId = null) {
    return this.notificationDispatcher.dispatchNotifications(
      triggeredAlerts,
      event,
      sourceType,
      serverId
    );
  }
}

export default AlertService;
